import { api } from "./api/apiSlice";

export interface Notification {
  id: number;
  title: string;
  message: string;
  type: "info" | "success" | "warning" | "error";
  targetAudience: "all" | "alumni" | "staff" | "specific";
  targetUserIds?: number[];
  status: "draft" | "scheduled" | "sent";
  scheduledAt?: string;
  sentAt?: string;
  readBy: number[];
  createdAt: string;
  updatedAt: string;
}

export interface GetNotificationsParams {
  skip?: number;
  limit?: number;
  is_read?: boolean;
}

export interface GetNotificationsResponse {
  notifications: Notification[];
  total: number;
  page: number;
  limit: number;
  unread_count: number;
}

export interface CreateNotificationRequest {
  title: string;
  message: string;
  type: Notification["type"];
  targetAudience: Notification["targetAudience"];
  targetUserIds?: number[];
  scheduledAt?: string;
}

export const notificationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getNotifications: builder.query<
      GetNotificationsResponse,
      GetNotificationsParams | void
    >({
      query: (params) => ({
        url: "notifications",
        params: params ?? undefined,
      }),
      providesTags: ["Notification", "UnreadCount"],
    }),

    getUnreadCount: builder.query<{ unread_count: number }, void>({
      query: () => "notifications/unread-count",
      providesTags: ["UnreadCount"],
    }),

    getNotificationById: builder.query<Notification, string>({
      query: (notificationId) => `notifications/${notificationId}`,
      providesTags: (result, error, id) => [{ type: "Notification", id }],
    }),

    markAsRead: builder.mutation<
      { message: string; notification_id: string },
      string
    >({
      query: (notificationId) => ({
        url: `notifications/${notificationId}/read`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "Notification", id },
        "Notification",
        "UnreadCount",
      ],
    }),

    markAllAsRead: builder.mutation<{ message: string; count: number }, void>({
      query: () => ({
        url: "notifications/read-all",
        method: "PUT",
      }),
      invalidatesTags: ["Notification", "UnreadCount"],
    }),

    createNotification: builder.mutation<
      Notification,
      CreateNotificationRequest
    >({
      query: (data) => ({
        url: "notifications",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Notification"],
    }),

    updateNotification: builder.mutation<
      Notification,
      { notificationId: string; data: Partial<CreateNotificationRequest> }
    >({
      query: ({ notificationId, data }) => ({
        url: `notifications/${notificationId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { notificationId }) => [
        { type: "Notification", id: notificationId },
        "Notification",
      ],
    }),

    deleteNotification: builder.mutation<void, string>({
      query: (notificationId) => ({
        url: `notifications/${notificationId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Notification", "UnreadCount"],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useGetUnreadCountQuery,
  useGetNotificationByIdQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useCreateNotificationMutation,
  useUpdateNotificationMutation,
  useDeleteNotificationMutation,
} = notificationApi;
